import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Modal,
  Table,
  Button,
  Checkbox,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  PanelSlider,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import {
  fetchConfluencePages,
  recrawlConfluencePage,
  deleteConfluencePage,
  createConfluencePageTag,
  fetchConfluencePageTags,
  saveConfluencePageTags,
  fetchConfluencePageAvailableTags
} from '../api';
import { StateLevel } from '.';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import { useBulkSelection } from './useBulkSelection';
import { runBulkAction, describeBulkResult } from './bulkActionUtils';
import BulkActionsBar from './BulkActionsBar';
import TagModal from './TagModal';

const ConfluencePagesList = ({
  spaceId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource');
  const canBulkSelect = canEditResource || (canViewTag && canEditTag);

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const bulk = useBulkSelection();
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkTagsModalVisible, setIsBulkTagsModalVisible] = useState(false);

  const order = useOrderableTableReducer({ id: `confluence-${spaceId}` });
  const pagination = usePaginationReducer({ id: `confluence-${spaceId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/confluence/pages',
      spaceId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchConfluencePages(spaceId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/confluence/groups/available'],
    queryFn: fetchConfluencePageAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_confluence_pages',
    createTagFn: createConfluencePageTag
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const pages = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const spaceName = response.space_name ?? 'Pages';
  const pageIds = useMemo(() => pages.map(page => page.id), [pages]);

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  const refetchPages = () => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/confluence/pages', spaceId] });
  };

  const findPage = (id) => pages.find(page => page.id === id);

  const handleBulkRecrawl = () => {
    const ids = bulk.selectedIds;
    if (ids.length === 0) {
      return;
    }
    setBulkModal({
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl ${ids.length} selected page${ids.length > 1 ? 's' : ''}?`}
          contentBody="This will update their content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(ids, recrawlConfluencePage);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'recrawl requested', 'page')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request recrawl for ${result.failed.length} page${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchPages();
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findPage(id)?.status !== 'Deleting');
    if (eligibleIds.length === 0) {
      showError('None of the selected pages can be deleted right now.');
      return;
    }
    setBulkModal({
      header: 'Confirm Delete',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete ${eligibleIds.length} selected page${eligibleIds.length > 1 ? 's' : ''}?`}
          contentBody="All synced data will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(eligibleIds, deleteConfluencePage);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'delete requested', 'page')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request delete for ${result.failed.length} page${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchPages();
        }
      }
    });
  };

  const openBulkTagsModal = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findPage(id)?.status === 'Success');
    if (eligibleIds.length === 0) {
      showError('None of the selected pages are ready to be tagged.');
      return;
    }
    const fetchedTags = availableTagsData?.data?.data ?? [];
    setTags(fetchedTags);
    setSelectedTags([]);
    setIsBulkTagsModalVisible(true);
  };

  const handleBulkTagConfirm = async () => {
    const eligibleIds = bulk.selectedIds.filter(id => findPage(id)?.status === 'Success');
    setBulkLoading(true);
    try {
      const result = await runBulkAction(eligibleIds, async (id) => {
        const assignedResponse = await fetchConfluencePageTags(id);
        const currentTagIds = (assignedResponse.data.data ?? []).map(tag => tag.id);
        const mergedTagIds = Array.from(new Set([...currentTagIds, ...selectedTags]));
        return saveConfluencePageTags({ pageId: id, groupIds: mergedTagIds });
      });
      if (result.succeeded.length > 0) {
        showSuccess(`${describeBulkResult(result, 'tagged', 'page')}.`);
      }
      if (result.failed.length > 0) {
        showError(`Failed to tag ${result.failed.length} page${result.failed.length > 1 ? 's' : ''}.`);
      }
      bulk.clear();
      refetchPages();
    } finally {
      setBulkLoading(false);
      setIsBulkTagsModalVisible(false);
      setSelectedTags([]);
    }
  };

  return (
    <AnimatePresence>
      <PanelSlider header={spaceName} handleHide={handleHide}>
        {isLoading && <LoadingContainer />}
        {!isLoading && !pages.length && (
          <Paragraph mb secondary>No pages available.</Paragraph>
        )}
        {!isLoading && pages.length > 0 && (
          <Flex col>
            <PermissionFilter can="edit generative ai resource">
              <BulkActionsBar
                count={bulk.count}
                onClear={bulk.clear}
                actions={[
                  <PermissionFilter can="view generative ai tag" key="bulk-tag">
                    <PermissionFilter can="edit generative ai tag">
                      <Button
                        icon="tag"
                        variant="secondary"
                        onClick={openBulkTagsModal}
                      >
                        Tags
                      </Button>
                    </PermissionFilter>
                  </PermissionFilter>,
                  <Button
                    key="bulk-recrawl"
                    icon="download"
                    variant="secondary"
                    loading={bulkLoading}
                    onClick={handleBulkRecrawl}
                  >
                    Recrawl
                  </Button>,
                  <Button
                    key="bulk-delete"
                    variant="secondary"
                    icon="trash"
                    colour="danger"
                    loading={bulkLoading}
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </Button>
                ]}
              />
            </PermissionFilter>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      ...(canBulkSelect ? [{
                        label: (
                          <Checkbox
                            aria-label="Select all pages"
                            checked={bulk.isAllSelected(pageIds)}
                            onChange={() => bulk.toggleAll(pageIds)}
                          />
                        )
                      }] : []),
                      { label: 'Title', sort: 'title' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {pages.map(page => (
                    <TableRow key={page.id}>
                      {canBulkSelect && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ${page.title}`}
                            checked={bulk.isSelected(page.id)}
                            onChange={() => bulk.toggle(page.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{page.title}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={page.status} />
                          <span>{page.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{page.updated_at}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {page.groups && page.groups.length > 0
                            ? page.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {page.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(page)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            {page.status !== 'Deleting' && (
                              <Button
                                colour="danger"
                                icon="trash"
                                loading={Boolean(loadingIds?.[page.id])}
                                onClick={async () => {
                                  try {
                                    await deleteConfluencePage(page.id);
                                    showSuccess('Page delete requested');
                                    refetchPages();
                                  } catch (error) {
                                    showError(`Failed to delete page: ${error.message}`);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            )}
                            <Button
                              icon="download"
                              loading={Boolean(loadingIds?.[page.id])}
                              onClick={() => onOpenRecrawlModal(page)}
                            >
                              Recrawl
                            </Button>
                          </PermissionFilter>

                          {errorIds?.[page.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[page.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}

            <TagModal
              isVisible={isBulkTagsModalVisible}
              header={`Tag ${bulk.count} Pages`}
              onCancel={() => {
                setIsBulkTagsModalVisible(false);
                setSelectedTags([]);
              }}
              onConfirm={handleBulkTagConfirm}
              tags={tags}
              selectedTags={selectedTags}
              handleTagChange={handleTagChange}
            />

            {bulkModal && (
              <Modal
                header={bulkModal.header}
                body={bulkModal.body}
                confirmText={bulkModal.confirmText}
                cancelText="Cancel"
                handleCancel={() => setBulkModal(null)}
                handleConfirm={bulkModal.handleConfirm}
                loading={bulkLoading}
              />
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ConfluencePagesList.propTypes = {
  spaceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object
};

export default ConfluencePagesList;
